import { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { RootState, dayjs } from '@umanforge/common';

import { ModifyTask, PostTask, Task, TaskFull } from 'interfaces/task.interface';
import { TaskService } from 'services/task.service';
import { TaskAction } from 'interfaces/taskAction.interface';
import { Location } from 'interfaces/location.interface';
import { LocationService } from 'services/location.service';
import { TaskActionService } from 'services/taskAction.service';
import { GroupedOption } from '@common_components/inputs/selects/multiSelectInput.componentPages';
import { RoleTaskService } from 'services/roleTask.service';
import { SectorSettings } from 'interfaces/setting.interface';
import { DEFAULT_VALUES } from 'constants/calendar.constants';
import { TaskEquipmentService } from 'services/task_equipment.service';
import { useEquipment } from './equipment.hook';
import { ModifyRecurrence, Recurrence } from 'interfaces/recurrence.interface';
import { RecurrenceService } from 'services/recurrence.service';
import { RoleTaskFull } from 'interfaces/roleTask.interface';

interface TaskErrors {
  date: string | undefined;
  location: string | undefined;
  taskAction: string | undefined;
  startHours: string | undefined;
  endHours: string | undefined;
  equipmentExpirationDate: string | undefined;
  requiredEquiSkills: string | undefined;
  requiredEquiAbilities: string | undefined;
  roleTasks: string | undefined;
}

export function useTaskModal(
  planningId: string,
  sectorId: string,
  onClose: () => void,
  taskId?: string,
  dateStartDrag?: dayjs.Dayjs,
  dateEndDrag?: dayjs.Dayjs,
  dateDrag?: dayjs.Dayjs,
  settings?: SectorSettings,
  onTaskCreate?: ( task: PostTask ) => Promise<string> | null,
  onUpdate?: ( task: ModifyTask ) => Promise<boolean>,
  onDeleteTask?: ( taskid: string ) => void | undefined
) {
  const token = useSelector( ( state: RootState ) => {return state.token.value;} );
  const { t } = useTranslation();
  const dispatch = useDispatch();


  const [ errors, setErrors ] = useState<TaskErrors>( {
    date: undefined,
    location: undefined,
    taskAction: undefined,
    startHours: undefined,
    endHours: undefined,
    equipmentExpirationDate: undefined,
    requiredEquiSkills: undefined,
    requiredEquiAbilities: undefined,
    roleTasks: undefined,
  } );

  // Form fields
  const [ selectedLocation, setSelectedLocation ] = useState<string>( );
  const [ selectedTaskAction, setSelectedTaskAction ] = useState<string>( );

  const [ dateStart, setDateStart ] = useState<dayjs.Dayjs | undefined>( dateStartDrag );
  const [ dateEnd, setDateEnd ] = useState<dayjs.Dayjs | undefined>( dateEndDrag );

  const [ timeStart, setTimeStart ] = useState<dayjs.Dayjs | undefined>( dateStartDrag );
  const [ timeEnd, setTimeEnd ] = useState<dayjs.Dayjs | undefined>( dateEndDrag );

  const [ recurrence, setRecurrence ] = useState<Recurrence>( );
  const [ fullDay, setFullDay ] = useState( false );

  // Modal
  const [ activeTab, setActiveTab ] = useState( 0 );
  const [ isDeleteModalOpen, setIsDeleteModalOpen ] = useState( false );
  const [ isDeleteRecurrenceModalOpen, setIsDeleteRecurrenceModalOpen ] = useState( false );
  const [ isConfirmationModalOpen, setIsConfirmationModalOpen, ] = useState( false );
  const [ isFormValid, setIsFormValid ] = useState( false );
  const [ isFormChanged, setIsFormChanged ] = useState( false );
  const [ isFirstRender, setIsFirstRender ] = useState( true );

  // External data
  const [ locations, setLocations ] = useState<Location[]>( [] );
  const [ taskActions, setTaskActions ] = useState<TaskAction[]>( [] );
  const [ task, setTask ] = useState<TaskFull>( );

  const [ recurrenceChanged, setRecurrenceChanged ] = useState<boolean>( false );
  const [ recurrenceValid, setRecurrenceValid ] = useState<boolean>( false );
  const [ recurrenceSaved, setRecurrenceSaved ] = useState<Recurrence>( );
  const [ recurrenceModalOpen, setRecurrenceModalOpen ] = useState<boolean>( recurrenceSaved ? true : false );

  const defaultRoleTask: RoleTaskFull = {
    abilities: [],
    skills: [],
    users: [],
    id: '',
    idTask: '',
    count: 1
  };
  const [ roleTasks, setRoleTasks ] = useState<RoleTaskFull[]>( taskId ? [] : [ { ...defaultRoleTask } ] );
  const [ roleTaskValid, setRoleTasksValid ] = useState<boolean>( true );
  const [ roleTasksChanged, setRoleTasksChanged ] = useState<boolean>( taskId ? false : true );

  const [ requiredEquiAbilities, setRequiredEquiAbilities ] = useState< string[] >( [] );
  const [ requiredEquiSkills, setRequiredEquiSkills ] = useState< { id: string; iluo: number | undefined }[] >( [] );

  const {
    selectedEquipments, setSelectedEquipments,
    equipmentAbilities,
    equipmentSkills,
    equipmentsOptions,
    equipmentsChanged,
    selectedEquipmentsSaved,
    getEquipmentErrors
  } = useEquipment(
    sectorId,
    taskId
  );

  // Use Memo
  const { calendarStep, calendarStartHour, calendarEndHour } = useMemo( () => {
    return {
      calendarStep: settings?.calendarStep?.value ?? DEFAULT_VALUES.STEP,
      calendarStartHour: settings?.calendarStartHour?.value ?? DEFAULT_VALUES.HOURSTART,
      calendarEndHour: settings?.calendarEndHour?.value ?? DEFAULT_VALUES.HOUREND,
    };
  }, [ settings ] );

  const dateAndTimeStart = useMemo( () => {
    return dateStart?.startOf( 'day' ).add( timeStart?.hour() ?? dayjs().hour(), 'hour' ).add( timeStart?.minute() ?? dayjs().minute(), 'minute' ) ?? dayjs();
  }, [ dateStart, timeStart ] );

  // Use Effects
  useEffect( () => {
    init();
  }, [ ] );


  useEffect( () => {
    setRecurrenceModalOpen( recurrenceSaved != null );
  }, [ recurrenceSaved ] );

  useEffect( () => {
    if ( !isFirstRender ) {
      const isValid = validateForm();
      const isChanged = checkFormChanged();

      setIsFormValid( isValid && isChanged );
      setIsFormChanged( isChanged );
    }
  }, [
    isFirstRender,
    selectedLocation,
    selectedTaskAction,
    dateStart,
    dateEnd,
    timeStart,
    timeEnd,
    recurrence,
    recurrenceChanged,
    task,
    taskId,
    dateStartDrag,
    dateEndDrag,
    dateDrag,
    fullDay,

    roleTasksChanged,
    roleTasks,

    equipmentsChanged,
    requiredEquiAbilities,
    requiredEquiSkills
  ] );

  const handleCloseModal = () => {
    onClose();
    document.body.style.overflow = 'unset';
  };

  const init = async () => {
    try {
      // edit task
      if ( taskId ) {
        const taskRes = await TaskService.getTaskFullById( taskId, token, dispatch );

        if ( taskRes ) {
          setTask( taskRes );
          setSelectedLocation( taskRes.location.id  );
          setSelectedTaskAction( taskRes.taskAction.id  );
          setDateStart( dayjs( taskRes.dateStart ) );
          setDateEnd( dayjs( taskRes.dateEnd ) );
          setTimeStart( dayjs( taskRes.dateStart ) );
          setTimeEnd( dayjs( taskRes.dateEnd ) );
          setFullDay( ( dayjs( taskRes.dateStart ).format( 'HH:mm' ) === '00:00' && dayjs( taskRes.dateEnd ).format( 'HH:mm' ) === '23:59' ) );

          if ( taskRes?.idRecurrence ) {
            const recurrenceRes = await RecurrenceService.getRecurrenceById( taskRes.idRecurrence, token, dispatch );
            if ( recurrenceRes ) {
              setRecurrence( recurrenceRes );
              setRecurrenceSaved( recurrenceRes );
              setRecurrenceModalOpen( true );
            }
          }
        }
      }
    }
    catch ( error ) {
      console.error( 'Error fetching task:', error );
    }

    if ( sectorId ) {
      try {
        const [
          locationsRes,
          taskActionsRes
        ] = await Promise.all( [
          LocationService.getLocationBySector( sectorId, token, dispatch ),
          TaskActionService.getTaskActionBySector( sectorId, token, dispatch )
        ] );

        setLocations( locationsRes );
        setTaskActions( taskActionsRes );
      }
      catch ( error ) {
        console.error( 'error fetch data', error );
      }
    }
    setIsFirstRender( false );
  };


  const handleRecurrenceChange = ( rec: Recurrence, isValid: boolean ) => {
    const change = hasRecurrenceChanged( rec );
    setRecurrenceChanged( change );
    if ( change ) {
      setRecurrence( rec );
    }
    setRecurrenceValid( isValid );
  };

  const hasRecurrenceChanged = ( rec: Recurrence ): boolean => {
    if ( !recurrenceModalOpen || !rec ) {
      return false;
    }
    if ( !recurrenceSaved ) {
      return true;
    }

    return (
      recurrenceSaved.type !== rec.type ||
      dayjs( recurrenceSaved.dateEnd ).startOf( 'day' ).toISOString() !== dayjs( rec.dateEnd ).startOf( 'day' ).toISOString() ||
      dayjs( recurrenceSaved.dateStart ).startOf( 'day' ).toISOString() !== dayjs( rec.dateStart ).startOf( 'day' ).toISOString() ||
      recurrenceSaved.value !== rec.value ||
      recurrenceSaved.repeat != rec.repeat ||
      recurrenceSaved.repeatType != rec.repeatType
    );
  };

  const onRoleTaskChange = ( roletasks: RoleTaskFull[], changed: boolean, isValid: boolean, requiredAbilities: string[], requiredSkills: { id: string; iluo: number | undefined }[] ) => {
    setRoleTasksValid( isValid && roleTasks.every( rt => { return rt.isValid === true || rt.isValid === undefined; } ) );
    setRequiredEquiSkills( requiredSkills );
    setRequiredEquiAbilities( requiredAbilities );
    if ( changed ) {
      setRoleTasks( roletasks );
    }
    setRoleTasksChanged( changed );
  };

  // Check if form has been changed in edit mode
  const checkEditModeTaskChanges = (): boolean => {
    let _dateStart = dateStart;
    let _dateEnd = dateEnd;

    if ( fullDay ) {
      _dateStart = dateStart?.startOf( 'day' );
      _dateEnd = dateEnd?.endOf( 'day' );
    }
    else if ( dateStart && timeStart && dateEnd && timeEnd ) {
      _dateStart = dateStart.startOf( 'day' ).add( timeStart.hour(), 'hour' ).add( timeStart.minute(), 'minute' );
      _dateEnd = dateEnd.startOf( 'day' ).add( timeEnd.hour(), 'hour' ).add( timeEnd.minute(), 'minute' );
    }

    const fullDayChanged = (
      ( dayjs( task?.dateStart ).format( 'HH:mm' ) === '00:00' && dayjs( task?.dateEnd ).format( 'HH:mm' ) === '23:59' ) !=
      (
        fullDay ||
        ( dayjs( _dateStart ).format( 'HH:mm' ) == '00:00' && dayjs( _dateEnd ).format( 'HH:mm' ) == '23:59' )
      )
    );

    return (
      equipmentsChanged ||
      roleTasksChanged ||
      selectedLocation != task?.location.id ||
      selectedTaskAction != task?.taskAction.id ||
      fullDayChanged ||
      ( _dateEnd != undefined && !dayjs( task?.dateEnd ).isSame( _dateEnd, 'minute' ) ) ||
      ( _dateStart != undefined && !dayjs( task?.dateStart ).isSame( _dateStart, 'minute' )  )
    );
  };

  const checkEditModeChanged = (): boolean => {
    return checkEditModeTaskChanges() || recurrenceChanged;
  };

  const checkCreateModeChanged = (): boolean => {
    // En mode création, on considère qu'il y a changement seulement si l'utilisateur a saisi ces valeurs
    return (
      selectedLocation != null && selectedLocation !== '' &&
      selectedTaskAction != null && selectedTaskAction !== ''
    );
  };

  const checkFormChanged = (): boolean => {
    return taskId ? checkEditModeChanged() : checkCreateModeChanged();
  };

  const validateForm = (): boolean => {
    const newErrors: TaskErrors = {
      date: undefined,
      location: undefined,
      taskAction: undefined,
      startHours: undefined,
      endHours: undefined,
      equipmentExpirationDate: undefined,
      requiredEquiSkills: undefined,
      requiredEquiAbilities: undefined,
      roleTasks: undefined,
    };

    newErrors.requiredEquiSkills = requiredEquiSkills.length > 0 ? t( 'translation:errors.required_equipment_skills' ) : undefined;
    newErrors.requiredEquiAbilities = requiredEquiAbilities.length > 0 ? t( 'translation:errors.required_equipment_authorizations' ) : undefined;
    if ( selectedEquipments.length > 0 && selectedEquipments.some( equi => { return dayjs( equi.object.expirationDate ).isBefore( dayjs().endOf( 'day' ) ); } ) ) {
      newErrors.equipmentExpirationDate = t( 'translation:errors.equipment_expiration_date_expired' );
    }


    if ( !dateStart || !timeStart ) {
      newErrors.startHours = t( 'translation:errors.no_time_start' );
    }
    if ( !dateEnd || !timeEnd ) {
      newErrors.endHours = t( 'translation:errors.no_time_start' );
    }

    let _dateStart = dateStart;
    let _dateEnd = dateEnd;

    if ( dateStart && timeStart && dateEnd && timeEnd ) {
      if ( fullDay ) {
        _dateStart = dateStart?.startOf( 'day' );
        _dateEnd = dateEnd?.endOf( 'day' );
      }
      else {
        _dateStart = dateStart.startOf( 'day' ).add( timeStart.hour(), 'hour' ).add( timeStart.minute(), 'minute' );
        _dateEnd = dateEnd.startOf( 'day' ).add( timeEnd.hour(), 'hour' ).add( timeEnd.minute(), 'minute' );
      }
    }


    if ( _dateStart?.isBefore( dayjs() ) ) {
      newErrors.startHours = t( 'translation:errors.you_cannot_create_task_in_the_past' );
    }
    if ( _dateEnd?.isBefore( dayjs() ) ) {
      newErrors.endHours = t( 'translation:errors.you_cannot_create_task_in_the_past' );
    }
    if ( _dateStart && _dateEnd && _dateEnd < _dateStart ) {
      newErrors.startHours = t( 'translation:errors.end_hour_is_below_hour_start' );
      newErrors.endHours = t( 'translation:errors.end_hour_is_below_hour_start' );
    }

    if ( !roleTaskValid ) {
      newErrors.roleTasks = t( 'translation:errors.roletasks_not_valid' );
    }

    setErrors( newErrors );

    return (
      !newErrors.date &&
      !newErrors.location &&
      !newErrors.taskAction &&
      !newErrors.startHours &&
      !newErrors.endHours &&
      !newErrors.roleTasks &&
      !newErrors.requiredEquiSkills &&
      !newErrors.requiredEquiAbilities &&
      recurrenceValid
    );
  };

  const handleSubmit = async ( updateRecurrence: boolean = false ) => {
    if ( errors.equipmentExpirationDate ) {
      setIsConfirmationModalOpen( true );
      return;
    }

    let _dateStart = dateStart;
    let _dateEnd = dateEnd;

    if ( fullDay ) {
      _dateStart = dateStart?.startOf( 'day' );
      _dateEnd = dateEnd?.endOf( 'day' );
    }
    else if ( dateStart && timeStart && dateEnd && timeEnd ) {
      _dateStart = dateStart.startOf( 'day' ).add( timeStart.hour(), 'hour' ).add( timeStart.minute(), 'minute' );
      _dateEnd = dateEnd.startOf( 'day' ).add( timeEnd.hour(), 'hour' ).add( timeEnd.minute(), 'minute' );
    }

    const base: Task = {
      id: taskId ?? '',
      idPlanning: planningId,
      idLocation: selectedLocation ?? '',
      idTaskAction: selectedTaskAction ?? '',
      status: 'COMING',
      debord: 0,
      nbSmallPackage: 0,
      nbBigPackage: 0,
      dateStart: _dateStart ? _dateStart.toISOString() : '',
      dateEnd: _dateEnd ? _dateEnd.toISOString() : '',
      idRecurrence: recurrence ? recurrence.id : undefined
    };

    let passed = false;
    let newIdTask = taskId;
    const shouldCreateRecurrence = recurrenceModalOpen && recurrenceChanged && !!recurrence && !recurrenceSaved;
    const shouldUpdateRecurrence = recurrenceModalOpen && recurrenceChanged && !!recurrenceSaved;

    if ( isFormValid ) {
      // create task
      if ( onTaskCreate && !taskId ) {
        const newTaskId = await onTaskCreate( base as PostTask );
        if ( newTaskId ) {
          newIdTask = newTaskId;
          passed = true;
        }
      }

      // update task
      if ( onUpdate && taskId ) {
        if ( shouldCreateRecurrence ) {
          const createdRecurrence = await RecurrenceService.postRecurrence( { ...recurrence, idTask: taskId } as Recurrence, token, dispatch )
            .catch( () => {return undefined;} );

          if ( createdRecurrence?.id ) {
            await TaskService.deleteTask( taskId, token, dispatch );
            passed = true;
            newIdTask = taskId;
          }
        }
        else {
          passed = await onUpdate( { ...base, id: taskId, idRecurrence: recurrenceSaved?.id ?? recurrence?.id } as ModifyTask );
          newIdTask = taskId;
        }
      }

      // update/push equipments, recurrence and role task of the task
      if ( newIdTask && passed ) {

        if ( roleTasks.length > 0 ) {
          passed = passed && await RoleTaskService.postRoleTaskFullByTask( newIdTask, RoleTaskService.fullToFullPost( roleTasks, newIdTask ), token, dispatch );
        }

        if ( selectedEquipments.length > 0 ) {
          passed = passed && await TaskEquipmentService.postEquipmentsByTask( newIdTask, { equipmentsId: selectedEquipments.map( equipment => { return equipment.object.id; } ) }, token, dispatch );
        }
        else if ( selectedEquipmentsSaved.length > 0 ) {
          await TaskEquipmentService.deleteEquipmentsOfTask( newIdTask, token, dispatch );
        }

        if ( shouldUpdateRecurrence && recurrenceSaved ) {
          // update existing recurrence when the rule itself changed,
          // even if the user used the default task update action
          await RecurrenceService.putRecurrence( recurrenceSaved.id, {
            dateEnd: recurrence?.dateEnd ?? recurrenceSaved.dateEnd,
            dateStart: recurrence?.dateStart ?? recurrenceSaved.dateStart,
            type: recurrence?.type ?? recurrenceSaved.type,
            value: recurrence?.value ?? recurrenceSaved.value,
            taskId: newIdTask,
            repeat: recurrence?.repeat ?? recurrenceSaved.repeat,
            repeatType: recurrence?.repeatType ?? recurrenceSaved.repeatType,
          } as ModifyRecurrence, token, dispatch );
        }

        if ( !taskId && shouldCreateRecurrence && recurrence ) {
          const createdRecurrence = await RecurrenceService.postRecurrence( { ...recurrence, idTask: newIdTask } as Recurrence, token, dispatch )
            .catch( () => {return undefined;} );

          if ( createdRecurrence?.id ) {
            await TaskService.deleteTask( newIdTask, token, dispatch );
          }
        }

        // Delete recurrence if it was saved and user has unchecked the recurrence modal
        else if ( updateRecurrence && recurrenceSaved ) {
          if ( newIdTask && passed && updateRecurrence && recurrenceSaved.id ) {
            const recurrencePayload: ModifyRecurrence = {
              taskId: newIdTask,
              value: recurrence?.value ?? recurrenceSaved.value,
              type: recurrence?.type ?? recurrenceSaved.type,
              repeat: recurrence?.repeat ?? recurrenceSaved.repeat,
              repeatType: recurrence?.repeatType ?? recurrenceSaved.repeatType,
            };

            if ( recurrenceChanged ) {
              recurrencePayload.dateStart = recurrence?.dateStart ?? recurrenceSaved.dateStart;
              recurrencePayload.dateEnd = recurrence?.dateEnd ?? recurrenceSaved.dateEnd;
            }

            await RecurrenceService.putRecurrence(
              recurrenceSaved.id,
              recurrencePayload,
              token,
              dispatch
            );
          }
        }
      }

      if ( passed ) {
        handleCloseModal();
      };
    }
  };

  const handleDelete = async ( updateRecurrence: boolean = false ) => {
    if ( taskId && onDeleteTask ) {
      if ( updateRecurrence && recurrenceSaved ) {
        await RecurrenceService.deleteRecurrence( recurrenceSaved.id, token, dispatch );
      }
      else {
        onDeleteTask( taskId );
      }
    }
    handleCloseModal();
  };


  const fetchLocation = (): Promise<GroupedOption[]> => {
    return Promise.resolve( [
      { options: locations.map( loc => {
        return { label: loc.name, id: loc.id };
      } ) }
    ] );
  };

  const fetchTaskAction = (): Promise<GroupedOption[]> => {
    return Promise.resolve( [
      { options: taskActions.map( ta => {
        return { label: ta.name, id: ta.id };
      } ) }
    ] );
  };

  const handleChangeDateStart = ( date: dayjs.Dayjs | undefined ) => {
    setDateStart( date );
  };

  const handleChangeDateEnd = ( date: dayjs.Dayjs | undefined ) => {
    setDateEnd( date );
  };

  return {
    errors,
    selectedLocation, setSelectedLocation,
    selectedTaskAction, setSelectedTaskAction,
    dateStart, setDateStart,
    dateEnd, setDateEnd,
    timeStart, setTimeStart,
    timeEnd, setTimeEnd,
    handleChangeDateStart, handleChangeDateEnd,
    recurrence, setRecurrence, recurrenceSaved,
    activeTab, setActiveTab,
    isDeleteModalOpen, setIsDeleteModalOpen,
    isDeleteRecurrenceModalOpen, setIsDeleteRecurrenceModalOpen,
    isConfirmationModalOpen, setIsConfirmationModalOpen,
    isFormValid,
    isFormChanged,
    hasTaskSpecificChanges: checkEditModeTaskChanges,
    locations,
    taskActions,
    selectedEquipments, setSelectedEquipments,
    handleSubmit,
    handleDelete,
    handleCloseModal,
    fetchLocation,
    fetchTaskAction,
    calendarStep,
    calendarStartHour,
    calendarEndHour,
    equipmentAbilities,
    equipmentSkills,
    equipmentsOptions,
    task,
    fullDay, setFullDay,
    recurrenceModalOpen, setRecurrenceModalOpen,
    recurrenceChanged, setRecurrenceChanged,
    recurrenceValid,
    handleRecurrenceChange,
    getEquipmentErrors,

    onRoleTaskChange,
    requiredEquiAbilities,
    requiredEquiSkills,
    dateAndTimeStart
  };
};
